import type { JSX } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useSidebarState } from './hooks/useSidebarState';
import { SidebarLogo } from './SidebarLogo';
import { NavItemLink } from './NavItemLink';

function MobileMenuButton({
  isMobileOpen,
  toggleMobile,
}: {
  isMobileOpen: boolean;
  toggleMobile: () => void;
}): React.JSX.Element {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="fixed left-4 top-4 z-50 md:hidden"
      onClick={toggleMobile}
      data-testid="mobile-menu-toggle"
    >
      {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
    </Button>
  );
}

function MobileOverlay({
  isMobileOpen,
  closeMobile,
}: {
  isMobileOpen: boolean;
  closeMobile: () => void;
}): React.JSX.Element | null {
  if (!isMobileOpen) return null;
  return (
    <div
      className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
      onClick={closeMobile}
    />
  );
}

function SidebarUserInfo({
  userName,
  displayRoles,
}: {
  userName: string | undefined;
  displayRoles: string[];
}): React.JSX.Element {
  return (
    <div data-testid="sidebar-user-info" className="p-4">
      <p className="text-xs text-muted-foreground">{userName}</p>
      <p className="text-xs text-muted-foreground">{displayRoles.join(', ')}</p>
    </div>
  );
}

export function Sidebar(): JSX.Element {
  const {
    isMobileOpen,
    isCollapsed,
    navItems,
    displayRoles,
    userName,
    toggleMobile,
    closeMobile,
    toggleCollapse,
    sidebarClassName,
    headerClassName,
    navClassName,
    collapseTooltip,
  } = useSidebarState();

  return (
    <>
      <MobileMenuButton isMobileOpen={isMobileOpen} toggleMobile={toggleMobile} />
      <MobileOverlay isMobileOpen={isMobileOpen} closeMobile={closeMobile} />
      <aside data-testid="sidebar" className={sidebarClassName}>
        <SidebarLogo
          isCollapsed={isCollapsed}
          headerClassName={headerClassName}
          collapseTooltip={collapseTooltip}
          toggleCollapse={toggleCollapse}
        />
        <nav className={navClassName}>
          {navItems.map((item) => (
            <NavItemLink
              key={item.path}
              item={item}
              onClick={closeMobile}
              isCollapsed={isCollapsed}
            />
          ))}
        </nav>
        <Separator />
        {!isCollapsed && <SidebarUserInfo userName={userName} displayRoles={displayRoles} />}
      </aside>
    </>
  );
}
